import React, { useContext, memo, useMemo } from "react";
import { Button, Space, Row, Col, Empty, Flex } from "antd";
import ClusterAccordionList from "./ClusterAccordionList";
import { campaignCreationContext } from "@ts/constants";
import ReviewCampaignsTable from "./ReviewCampaignsTable";
import StepHeader from "./StepHeader";
import { BCC_MODULE_TYPES, CLUSTER_ACCORDION_TYPES, CREATE_CAMPAIGN_HANDLER_ACTIONS, IClusterFormData, IProductGroup } from "@/ts/common-types";
import { productDataSorter } from "@/ts/utils";

const RightPanel: React.FC = memo(() => {
    const {
        createCampaignsHandler,
        selectedClusters,
        clusters,
        currentStep,
        selectedProductIds,
        productGroups: allProducts,
        isNextStepDisabled,
        isPublishingCampaigns,
    } = useContext(campaignCreationContext);
    // Get selected cluster data
    const selectedClusterData = clusters
        .filter((cluster: IClusterFormData) => selectedClusters.includes(cluster.id))
        .map((cluster: IClusterFormData) => ({
            id: cluster.id,
            name: cluster.name || '',
            products: cluster.products?.length || 0,
            type: CLUSTER_ACCORDION_TYPES.CLUSTER,
        }));

    // Transform selected products into clusters
    const selectedProductData = allProducts
        .filter((product: IProductGroup) => selectedProductIds.includes(product.id))
        .map((product: IProductGroup) => ({
            id: `##productCluster_${product.id}`,
            name: product.title,
            products: 1,
            type: CLUSTER_ACCORDION_TYPES.PRODUCT,
        }));

    // Combine clusters and products
    const allSelectedItems = [...selectedClusterData, ...selectedProductData];

    const clusterCount = selectedClusterData.length;
    const productCount = selectedProductData.length;

    const prepareReviewData = useMemo(() => {
        if (currentStep !== 2) return [];

        // Get selected product clusters (IDs starting with ##productCluster_)
        const productClusterIds = selectedProductIds.map(
            (productId: string) => `##productCluster_${productId}`
        );

        const allSelectedClusterIds = [...selectedClusters, ...productClusterIds];

        return clusters
            .filter((cluster: IClusterFormData) => allSelectedClusterIds.includes(cluster.id))
            .map((cluster: IClusterFormData) => {
                const collections = cluster.collections || [];
                const isProductCluster = cluster.id.startsWith("##productCluster_");

                // Aggregate all campaigns from all collections under this cluster
                const allCampaignsFromCollections = collections.flatMap(
                    (collection) => {
                        return (collection.campaigns || [])
                            .filter((campaign) => !campaign.isArchived)
                            .map((campaign) => ({
                                ...campaign,
                                collectionId: collection.id,
                                collectionCampaignType: collection.campaignType,
                            }));
                    }
                );

                // Calculate total budget from all collections
                const totalBudget = collections.reduce(
                    (sum: number, collection) =>
                        sum + (Number(collection.budget) || 0),
                    0
                );

                // Calculate total campaigns count (excluding archived)
                const totalCampaigns = allCampaignsFromCollections.length;

                // Row data - cluster level info (grouping all collections)
                const rowData = {
                    key: cluster.id,
                    clusterId: cluster.id,
                    clusterName: cluster.name,
                    clusterType: isProductCluster ? CLUSTER_ACCORDION_TYPES.PRODUCT : CLUSTER_ACCORDION_TYPES.CLUSTER,
                    productsCount: cluster.products?.length || 0,
                    budget: totalBudget.toString(),
                    startDate: cluster.startDate
                        ? cluster.startDate.format("DD/MM/YYYY")
                        : "",
                    endDate: "-", // Set as "-" for now
                    totalCampaigns,
                };

                // Expanded data - each campaign is already a cohort x city combination
                // No need to create additional combinations
                const expandedData = allCampaignsFromCollections.map(
                    (campaign, index: number) => {
                        const keywordTypes = Array.isArray(campaign.keywordType)
                            ? campaign.keywordType
                            : campaign.keywordType
                            ? [campaign.keywordType]
                            : [];
                        
                        const cities = Array.isArray(campaign.city)
                            ? campaign.city
                            : campaign.city
                            ? [campaign.city]
                            : [];

                        // Helper function to format city name, handling cohort/region/tier/zone format
                        const formatCityName = (city: string): string => {
                            // Handle cohort format: cohort:level:displayLabel:cityIds
                            if (city?.startsWith('cohort:')) {
                                const parts = city.slice(7).split(':');
                                if (parts.length >= 2) {
                                    return parts[1]; // Return the display label
                                }
                                // Legacy format fallback
                                return city;
                            }
                            // Handle region format: region:displayLabel:cityIds
                            if (city?.startsWith('region:')) {
                                const parts = city.slice(7).split(':');
                                return parts[0] || city; // Return the display label
                            }
                            // Handle tier format: tier:displayLabel:cityIds
                            if (city?.startsWith('tier:')) {
                                const parts = city.slice(5).split(':');
                                return parts[0] || city; // Return the display label
                            }
                            // Handle zone format: zone:displayLabel:cityIds
                            if (city?.startsWith('zone:')) {
                                const parts = city.slice(5).split(':');
                                return parts[0] || city; // Return the display label
                            }
                            const parts = city.split("_");
                            return parts[2] || city;
                        };

                        // Helper to extract display names from cities array
                        const formatCityDisplay = (citiesArr: string[]): string => {
                            let cohortEntry: string | undefined;
                            let regionEntry: string | undefined;
                            let tierEntry: string | undefined;
                            let zoneEntry: string | undefined;

                            for (const city of citiesArr) {
                                if (city?.startsWith('cohort:') && !cohortEntry) {
                                    cohortEntry = city;
                                } else if (city?.startsWith('region:') && !regionEntry) {
                                    regionEntry = city;
                                } else if (city?.startsWith('tier:') && !tierEntry) {
                                    tierEntry = city;
                                } else if (city?.startsWith('zone:') && !zoneEntry) {
                                    zoneEntry = city;
                                }
                                // Early exit if all entries are found
                                if (cohortEntry && regionEntry && tierEntry && zoneEntry) {
                                    break;
                                }
                            }
                            
                            if (cohortEntry) {
                                return formatCityName(cohortEntry);
                            }
                            if (regionEntry) {
                                return formatCityName(regionEntry);
                            }
                            if (tierEntry) {
                                return formatCityName(tierEntry);
                            }
                            if (zoneEntry) {
                                return formatCityName(zoneEntry);
                            }
                            return citiesArr.map(formatCityName).join(", ");
                        };

                        // Use cohortLabel if available, otherwise join keywordTypes
                        const keywordTypesStr = campaign.cohortLabel || keywordTypes.join(", ");
                        const cityNames = formatCityDisplay(cities);

                        return {
                            key: `${cluster.id}-campaign-${campaign.id || index}`,
                            id: campaign.id,
                            clusterId: cluster.id,
                            collectionId: campaign.collectionId,
                            campaignName: campaign.name || `${cluster.name} - ${keywordTypesStr}`,
                            budget: campaign.budget || "",
                            campaignType: campaign.collectionCampaignType || "",
                            keywordType: keywordTypesStr,
                            city: cityNames,
                            timeSlot: "All Day",
                            cohortLabel: campaign.cohortLabel,
                        };
                    }
                );

                return {
                    rowData,
                    expandedData,
                };
            })
            .filter((item) => item.expandedData.length > 0); // Only include clusters with campaigns
    }, [clusters, selectedClusters, selectedProductIds, currentStep]);

    const totalCampaignsCount = useMemo(() => {
        return prepareReviewData.reduce(
            (sum: number, item) => sum + item.rowData.totalCampaigns,
            0
        );
    }, [prepareReviewData]);

    const productDataSorterFunction = <T,>(
        data: T[],
        originalData: T[],
        sortConfig: { sorter?: (a: T, b: T) => number; order?: 'ascend' | 'descend' },
    ): T[] => {
        return productDataSorter(data, originalData, sortConfig, [], BCC_MODULE_TYPES.CLUSTER);
    };

    return (
        <div className="right-panel">
            <Row style={{ height: "100%", overflow: "hidden" }}>
                <Col span={24} className="d-flex flex-column">
                    <div className="collections-area">
                        <StepHeader
                            {...{
                                currentStep,
                                clusterCount,
                                productCount,
                                totalCampaignsCount,
                            }}
                            showCounts={allSelectedItems.length > 0}
                        />
                        {allSelectedItems.length === 0 ? (
                            <Empty
                                description="Select Clusters or Products to start building Collections"
                                className="collections-empty"
                            />
                        ) : (
                            <Space
                                direction="vertical"
                                style={{ width: "100%", gap: "16px" }}
                            >
                                {currentStep === 2 ? (
                                    <ReviewCampaignsTable
                                        prepareReviewData={prepareReviewData}
                                        isLoading={false}
                                    />
                                ) : (
                                    <ClusterAccordionList
                                        list={allSelectedItems}
                                        productSorter={productDataSorterFunction}
                                        type={
                                            currentStep === 0
                                                ? CLUSTER_ACCORDION_TYPES.PRODUCT
                                                : CLUSTER_ACCORDION_TYPES.CLUSTER
                                        }
                                    />
                                )}
                            </Space>
                        )}
                    </div>
                    <Flex
                        className="panel-footer"
                        gap="middle"
                        justify="space-between"
                    >
                        <Button
                            type="default"
                            size="large"
                            onClick={() => createCampaignsHandler(CREATE_CAMPAIGN_HANDLER_ACTIONS.BACK)}
                            className="next-button"
                        >
                            Back
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() =>
                                createCampaignsHandler(
                                    currentStep === 2 ? CREATE_CAMPAIGN_HANDLER_ACTIONS.PUBLISH : CREATE_CAMPAIGN_HANDLER_ACTIONS.NEXT
                                )
                            }
                            className="next-button"
                            disabled={isNextStepDisabled({
                                clusterCount,
                                productCount,
                            })}
                            loading={isPublishingCampaigns}
                        >
                            {currentStep === 2
                                ? `Publish ${totalCampaignsCount <= 1 ? 'Campaign' : 'Campaigns'} (${totalCampaignsCount})`
                                : "Next: Create Campaigns"}
                        </Button>
                    </Flex>
                </Col>
            </Row>
        </div>
    );
});

export default RightPanel;